var _dec, _class, _descriptor;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Service, { inject as service } from '@ember/service';
import { jsonPost } from '@square/dashboard-utilities/utils/xhr';
import { isPresent } from '@ember/utils';
import { camelize, underscore } from '@square/dashboard-utilities/utils/keys';
import { A } from '@ember/array';
import DeviceProfileError, { DeviceProfileMessage } from '@square/dashboard-data/models/device-profiles-v2/device-profile-error';
import { Status } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/status';
import { captureException } from '@sentry/ember';
import { LinkDeviceProfilesV2Request, LinkDeviceProfilesV2Response } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/link_device_profiles';
import { GetDeviceProfileLinksV2Request, GetDeviceProfileLinksV2Response } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/get_device_profile_links';
import { Pagination } from '@square/dashboard-protos/models/squareup/devicesettings/profiles/v2/message/pagination';
const DEVICE_PROFILE_V2_URL_PREFIX = '/2.0/device_profiles';
const DEVICE_PROFILE_LINK_CREATE_LINK_URL = `${DEVICE_PROFILE_V2_URL_PREFIX}/link`;
const DEVICE_PROFILE_LINK_GET_LINKS_URL = `${DEVICE_PROFILE_V2_URL_PREFIX}/get_links`;
const SUCCESSFUL_STATUSES = new Set([Status.SUCCESS, Status.NO_LINKS_FOUND // the default response status when no links are created yet
]);
const MAX_DEVICE_LINKS_RESULTS = 100;
const MAX_DEVICE_LINKS_REQUEST = 100;
const MAX_PARALLEL_REQUESTS = 5;
let DeviceProfileLink = (_dec = service('device-profiles-v2/cdp-events'), _class = class DeviceProfileLink extends Service {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "cdpEventsService", _descriptor, this);
  }
  createBatchLinkDevicesToProfile(batchLinkDevicesToProfile) {
    return this._createOrUpdateDeviceProfileLink([], batchLinkDevicesToProfile);
  }
  createOrUpdateDeviceProfileLink(deviceLinks) {
    const batchQueue = this._chunkArray(deviceLinks, MAX_DEVICE_LINKS_REQUEST);
    const _createOrUpdateDeviceProfileLink = this._createOrUpdateDeviceProfileLink.bind(this);
    async function _processRequests(queue, count) {
      const batches = [];
      while (batches.length < count && queue.length > 0) {
        batches.push(queue.pop());
      }
      const inFlightRequests = batches.map(batch => {
        return _createOrUpdateDeviceProfileLink(batch);
      });
      const responses = await Promise.all(inFlightRequests);
      const linkedProfileLinks = responses.reduce((acc, response) => {
        const {
          linkedProfileLinks
        } = response;
        return [...acc, ...linkedProfileLinks];
      }, []);
      return linkedProfileLinks;
    }
    const processRequests = _processRequests.bind(this);
    async function _processQueue(queue, acc) {
      const pairedLinks = await processRequests(queue, MAX_PARALLEL_REQUESTS);
      const totalPairedLinks = [...acc, ...pairedLinks];
      if (queue.length > 0) {
        return _processQueue(queue, totalPairedLinks);
      }
      return LinkDeviceProfilesV2Response.create({
        linkedProfileLinks: totalPairedLinks,
        unlinkedProfileLinks: []
      });
    }
    const processQueue = _processQueue.bind(this);
    return processQueue(batchQueue, []);
  }
  _createOrUpdateDeviceProfileLink(deviceLinks, batchLinkDevicesToProfile) {
    const linkDeviceProfilesV2Request = LinkDeviceProfilesV2Request.create({
      deviceProfileLinks: deviceLinks,
      batchLink: batchLinkDevicesToProfile
    });
    const json = underscore(linkDeviceProfilesV2Request.toJSON());
    return jsonPost(DEVICE_PROFILE_LINK_CREATE_LINK_URL, json).then(resp => {
      const proto = LinkDeviceProfilesV2Response.fromObject(camelize(resp));
      if (!this.isSuccess(proto.status)) {
        this.cdpEventsService.maybeTrackProfileFetchUnexpectedStatus({
          status: Status[proto.status]
        });
        throw new DeviceProfileMessage(`Error creating/updating device profile links -- Status: ${proto.status}`, Status[proto.status]);
      }
      return proto;
    });
  }
  async getDeviceProfileLinks(deviceProfileType = undefined, cursor = undefined, objects = []) {
    const {
      links,
      nextCursor
    } = await this._getDeviceProfileLinks(deviceProfileType, cursor, MAX_DEVICE_LINKS_RESULTS);
    const newFetchedLinks = A([...objects, ...links]);
    return isPresent(nextCursor) && nextCursor !== 0 ? this.getDeviceProfileLinks(deviceProfileType, nextCursor, newFetchedLinks) : newFetchedLinks;
  }
  _getDeviceProfileLinks(deviceProfileType, cursor, maxResults) {
    const getDeviceProfileLinksRequest = new GetDeviceProfileLinksV2Request.Builder().deviceProfileType(deviceProfileType).pagination(Pagination.create({
      cursor,
      maxResults
    })).build();
    const json = underscore(getDeviceProfileLinksRequest.toJSON());
    return jsonPost(DEVICE_PROFILE_LINK_GET_LINKS_URL, json).then(resp => {
      const proto = GetDeviceProfileLinksV2Response.fromObject(camelize(resp));
      if (!this.isSuccess(proto.status)) {
        this.cdpEventsService.maybeTrackProfileFetchUnexpectedStatus({
          status: Status[proto.status]
        });
        throw new DeviceProfileMessage(`Error fetching device profile links -- Status: ${proto.status}`, Status[proto.status]);
      }
      return proto;
    }).catch(err => {
      captureException(err);
      throw new DeviceProfileError('Error fetching device profile links', err);
    });
  }
  isSuccess(status) {
    return SUCCESSFUL_STATUSES.has(status);
  }
  _chunkArray(array, chunkSize) {
    const arrays = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      arrays.push(array.slice(i, i + chunkSize));
    }
    return arrays;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "cdpEventsService", [_dec], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _class);
export { DeviceProfileLink as default };